// Shared low-poly village parts — a common palette, a gable-roof builder with
// real eaves overhang (ridge along x or z), a pole-and-plank construction
// scaffold (the "site in progress" look), a shuttered window and a plump tied
// grain sack. Ported from age-of-mcp's v2/village-parts.ts: the pure geometry is
// kept verbatim, with the Part type taken from the library. Authored in
// footprint tile units — no DOM, no scene access.

import * as THREE from "three";
import type { Part } from "../types";

/** Shared village palette. */
export const PLASTER   = "#e9dfc6";   // limewashed infill
export const TIMBER    = "#55422a";   // dark oak framing
export const ROOF_TILE = "#9c5232";   // warm clay tile
export const ROOF_DARK = "#6e3a24";   // ridge beams & roof trim
export const SCAFFOLD  = "#8a6b3c";   // fresh work-timber

/**
 * A pitched gable roof sitting on a wall top at `topY`: two sloped planes
 * meeting at a ridge beam, with eaves overhang all round. `axis` is the
 * RIDGE direction — "x" (planes fall toward ±z) or "z" (planes fall toward
 * ±x, so the gable end faces the street).
 */
export function gableRoof(o: {
  cx: number; cz: number; topY: number;
  /** Full span eave-to-eave across the slope, before overhang. */
  span: number;
  /** Full length along the ridge, before overhang. */
  length: number;
  rise: number;
  axis?: "x" | "z";
  color?: string;
  ridgeColor?: string;
}): Part[] {
  const axis = o.axis ?? "x";
  const color = o.color ?? ROOF_TILE;
  const ridgeColor = o.ridgeColor ?? ROOF_DARK;
  const half = o.span / 2 + 0.06;            // eaves overhang across the slope
  const len = o.length + 0.12;               // overhang along the ridge
  const slope = Math.hypot(half, o.rise);
  const ang = Math.atan2(o.rise, half);
  const midY = o.topY + o.rise / 2;
  if (axis === "x") {
    return [
      { geometry: new THREE.BoxGeometry(len, 0.06, slope), color,
        position: [o.cx, midY, o.cz + half / 2], rotation: [ang, 0, 0] },
      { geometry: new THREE.BoxGeometry(len, 0.06, slope), color,
        position: [o.cx, midY, o.cz - half / 2], rotation: [-ang, 0, 0] },
      { geometry: new THREE.BoxGeometry(len + 0.04, 0.07, 0.07), color: ridgeColor,
        position: [o.cx, o.topY + o.rise, o.cz] },
    ];
  }
  // Ridge along z: the planes fall toward ±x (rotation about z; a positive
  // z-rotation raises a box's +x end, so the +x-side plane rotates by -ang).
  return [
    { geometry: new THREE.BoxGeometry(slope, 0.06, len), color,
      position: [o.cx + half / 2, midY, o.cz], rotation: [0, 0, -ang] },
    { geometry: new THREE.BoxGeometry(slope, 0.06, len), color,
      position: [o.cx - half / 2, midY, o.cz], rotation: [0, 0, ang] },
    { geometry: new THREE.BoxGeometry(0.07, 0.07, len + 0.04), color: ridgeColor,
      position: [o.cx, o.topY + o.rise, o.cz] },
  ];
}

/**
 * The construction scaffold around a `w`×`d` build centred at (cx, cz):
 * four corner poles with a pair of walk-planks lashed across, so a site
 * reads as carpentry in progress rather than four bare sticks.
 */
export function buildScaffold(
  w: number, d: number, h: number, cx = 0, cz = 0,
): Part[] {
  const ox = w / 2, oz = d / 2;
  const out: Part[] = [];
  for (const [x, z] of [[-ox, -oz], [ox, -oz], [-ox, oz], [ox, oz]] as const) {
    out.push({
      geometry: new THREE.CylinderGeometry(0.025, 0.025, h, 4),
      color: SCAFFOLD,
      position: [cx + x, h / 2, cz + z],
    });
  }
  const py = h * 0.66;
  out.push(
    { geometry: new THREE.BoxGeometry(w + 0.08, 0.03, 0.1), color: SCAFFOLD,
      position: [cx, py, cz - oz] },
    { geometry: new THREE.BoxGeometry(0.1, 0.03, d + 0.08), color: SCAFFOLD,
      position: [cx + ox, py * 0.8, cz] },
  );
  return out;
}

/** A shuttered window on a +z-facing wall: dark glass between two shutters. */
export function shutterWindow(x: number, y: number, z: number, w = 0.16, h = 0.16): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(w, h, 0.04), color: "#39444e", position: [x, y, z] },
    { geometry: new THREE.BoxGeometry(w * 0.45, h * 1.05, 0.03), color: TIMBER,
      position: [x - w * 0.72, y, z] },
    { geometry: new THREE.BoxGeometry(w * 0.45, h * 1.05, 0.03), color: TIMBER,
      position: [x + w * 0.72, y, z] },
  ];
}

/** A plump tied grain sack standing on `base` (a body and a cinched neck). */
export function sack(x: number, z: number, base: number, s = 1): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.085 * s, 0.105 * s, 0.2 * s, 6),
      color: "#cdb27a", position: [x, base + 0.1 * s, z] },
    { geometry: new THREE.CylinderGeometry(0.03 * s, 0.05 * s, 0.06 * s, 6),
      color: "#b89a62", position: [x, base + 0.22 * s, z] },
  ];
}
